import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Link,
  IconButton,
  makeStyles,
  useTheme,
  Slide,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useCookie } from "../../context/CookieContext";
import { i18n } from "../../translate/i18n";

import { Link as RouterLink } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "fixed",
    bottom: 24,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    zIndex: 9999,
    padding: theme.spacing(0, 2),
    pointerEvents: "none", // Permite clicar "atrás" do container, exceto no banner
  },
  banner: {
    pointerEvents: "auto", // Reativa cliques no banner
    width: "100%",
    maxWidth: 800,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
    borderRadius: 16,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
  cookieImage: {
    width: 24,
    height: 24,
    [theme.breakpoints.down("sm")]: {
      width: 20,
      height: 20,
    },
  },
  content: {
    flex: 1,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  prefItem: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  prefHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  btnSecondary: {
    textTransform: "none",
    fontWeight: 600,
  },
  btnPrimary: {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 8,
  },
  dialogPaper: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: 20,
    border: `1px solid ${theme.palette.divider}`,
    backgroundImage: "none", // Remove overlay do dark mode do MUI se houver
  },
  dialogTitle: {
    padding: theme.spacing(3, 3, 1),
    "& h6": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      fontSize: "1.5rem",
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
  },
  dialogSubtitle: {
    padding: theme.spacing(0, 3, 3),
    color: theme.palette.text.secondary,
    fontSize: "0.95rem",
    lineHeight: 1.5,
  },
  prefItem: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    borderRadius: 12,
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      borderColor: theme.palette.primary.main,
    },
  },
  prefHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(0.5),
  },
  prefTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: theme.palette.text.primary,
  },
  prefDesc: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    lineHeight: 1.4,
    maxWidth: "85%",
  },
  dialogFooter: {
    padding: theme.spacing(3),
    backgroundColor: "transparent",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(2.5),
  },
  footerDisclaimer: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      fontWeight: 600,
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  footerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  btnGroup: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  btnDeclineText: {
    color: theme.palette.text.primary,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.9rem",
    padding: 0,
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
      color: theme.palette.primary.main,
    },
  },
  btnAcceptOutlined: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 10,
    padding: "8px 24px",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
    },
  },
  btnSavePrimary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || "#fff",
    textTransform: "none",
    fontWeight: 800,
    borderRadius: 10,
    padding: "10px 24px",
    boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}66`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      transform: "translateY(-1px)",
      boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}88`,
    },
  },
  switchBase: {
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
    "&.Mui-checked": {
      color: theme.palette.primary.main,
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.3,
    },
  },
}));

const CookieConsent = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { consent, showSettings, setShowSettings, acceptAll, declineNonEssential, saveConsent } = useCookie();
  const [prefs, setPrefs] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  const handleSaveSettings = () => {
    saveConsent({
      ...prefs,
      essential: true,
      date: new Date().toISOString(),
    });
  };

  // If user hasn't consented and settings modal is closed, show banner
  // If user has consented but wants to change settings, show only modal
  const showBanner = !consent && !showSettings;

  return (
    <>
      <Box className={classes.container}>
        <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
          <Box className={classes.banner}>
            <Box className={classes.content}>
              <Typography variant="h6" gutterBottom style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
                <img src="/cookies.png" alt="" className={classes.cookieImage} />
                {i18n.t("cookieConsent.title")}
              </Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("cookieConsent.message")}{" "}
              <Link 
                component={RouterLink} 
                to="/privacy" 
                className={classes.link}
              >
                {i18n.t("cookieConsent.policyLink")}
              </Link>
            </Typography>
            </Box>
          <Box className={classes.actions}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.btnSecondary}
              onClick={() => setShowSettings(true)}
            >
              {i18n.t("cookieConsent.buttons.configure")}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              className={classes.btnSecondary}
              onClick={declineNonEssential}
            >
              {i18n.t("cookieConsent.buttons.decline")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.btnPrimary}
              onClick={acceptAll}
            >
              {i18n.t("cookieConsent.buttons.acceptAll")}
            </Button>
          </Box>
        </Box>
      </Slide>
    </Box>

      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box display="flex" alignItems="center" gap={1.5}>
              <img src="/cookies.png" alt="" style={{ width: 32, height: 32 }} />
              <Typography variant="h6" style={{ fontWeight: 800 }}>
                {i18n.t("cookieConsent.title")}
              </Typography>
            </Box>
            <IconButton onClick={() => setShowSettings(false)} style={{ color: "rgba(255,255,255,0.5)" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Box className={classes.dialogSubtitle}>
          {i18n.t("cookieConsent.subtitle")}
        </Box>

        <DialogContent style={{ border: "none", padding: "0 24px" }}>
          {/* Categoria: Essenciais */}
          <Box className={classes.prefItem}>
            <Box className={classes.prefHeader}>
              <Box>
                <Typography className={classes.prefTitle}>
                  {i18n.t("cookieConsent.categories.essential.title")}
                </Typography>
                <Typography className={classes.prefDesc}>
                  {i18n.t("cookieConsent.categories.essential.description")}
                </Typography>
              </Box>
              <Switch checked disabled classes={{ thumb: classes.switchBase, track: classes.switchBase }} />
            </Box>
          </Box>

          {/* Categoria: Funcionais */}
          <Box className={classes.prefItem}>
            <Box className={classes.prefHeader}>
              <Box>
                <Typography className={classes.prefTitle}>
                  {i18n.t("cookieConsent.categories.functional.title")}
                </Typography>
                <Typography className={classes.prefDesc}>
                  {i18n.t("cookieConsent.categories.functional.description")}
                </Typography>
              </Box>
              <Switch
                checked={prefs.functional}
                onChange={(e) => setPrefs({ ...prefs, functional: e.target.checked })}
                color="primary"
                classes={{ thumb: classes.switchBase, track: classes.switchBase }}
              />
            </Box>
          </Box>

          {/* Categoria: Analíticos */}
          <Box className={classes.prefItem}>
            <Box className={classes.prefHeader}>
              <Box>
                <Typography className={classes.prefTitle}>
                  {i18n.t("cookieConsent.categories.analytics.title")}
                </Typography>
                <Typography className={classes.prefDesc}>
                  {i18n.t("cookieConsent.categories.analytics.description")}
                </Typography>
              </Box>
              <Switch
                checked={prefs.analytics}
                onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                color="primary"
                classes={{ thumb: classes.switchBase, track: classes.switchBase }}
              />
            </Box>
          </Box>

          {/* Categoria: Marketing */}
          <Box className={classes.prefItem}>
            <Box className={classes.prefHeader}>
              <Box>
                <Typography className={classes.prefTitle}>
                  {i18n.t("cookieConsent.categories.marketing.title")}
                </Typography>
                <Typography className={classes.prefDesc}>
                  {i18n.t("cookieConsent.categories.marketing.description")}
                </Typography>
              </Box>
              <Switch
                checked={prefs.marketing}
                onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                color="primary"
                classes={{ thumb: classes.switchBase, track: classes.switchBase }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions className={classes.dialogFooter}>
          <Typography className={classes.footerDisclaimer}>
            {i18n.t("cookieConsent.disclaimer")} {" "}
            <Link component={RouterLink} to="/privacy" onClick={() => setShowSettings(false)}>
              {i18n.t("cookieConsent.policyLink")}
            </Link>
          </Typography>

          <Box className={classes.footerActions}>
            <Button className={classes.btnDeclineText} onClick={declineNonEssential}>
              {i18n.t("cookieConsent.buttons.decline")}
            </Button>
            
            <Box className={classes.btnGroup}>
              <Button variant="outlined" className={classes.btnAcceptOutlined} onClick={acceptAll}>
                {i18n.t("cookieConsent.buttons.acceptAll")}
              </Button>
              <Button variant="contained" className={classes.btnSavePrimary} onClick={handleSaveSettings}>
                {i18n.t("cookieConsent.buttons.save")}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
